import React from 'react';
import { FileText, Download, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RawFileViewResponse } from '@/types/api';
import { useToast } from '@/hooks/use-toast';

interface TextViewerProps {
  fileData: RawFileViewResponse;
}

export function TextViewer({ fileData }: TextViewerProps) {
  const { toast } = useToast();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fileData.content);
      toast({
        title: "Copied to clipboard",
        description: "File content has been copied to clipboard.",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy content to clipboard.",
        variant: "destructive",
      });
    }
  };

  const downloadFile = () => {
    try {
      const blob = new Blob([fileData.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Download started",
        description: `Downloading ${fileData.filename}...`,
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Could not download the file.",
        variant: "destructive",
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <CardTitle className="text-lg truncate">{fileData.filename}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{fileData.file_type.toUpperCase()}</Badge>
          </div>
        </div>
        
        {/* File Info */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{formatFileSize(fileData.size_bytes)}</span>
          <span>{fileData.lines} lines</span>
          <span>Modified: {formatDate(fileData.modified)}</span>
          <span>Encoding: {fileData.encoding}</span>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col min-h-0">
        {/* Controls */}
        <div className="flex items-center justify-end gap-2 mb-4 pb-4 border-b">
          <Button
            variant="outline"
            size="sm"
            onClick={copyToClipboard}
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={downloadFile}
          >
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>

        {/* Text Content */}
        <ScrollArea className="flex-1 bg-gray-50 rounded-md p-4">
          <div className="font-mono text-sm">
            {fileData.content.split('\n').map((line, index) => (
              <div key={index} className="flex">
                <span className="text-gray-400 text-right pr-4 select-none min-w-[3rem]">
                  {index + 1}
                </span>
                <span className="flex-1 whitespace-pre-wrap break-words">
                  {line || '\u00A0'} {/* Non-breaking space for empty lines */}
                </span>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}